import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";

import { Button, Loader, Modal, Segment, Table } from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";

import api from "api";
import { useAppSelector } from "store";
import { sdiClickDomain } from "utils/domains";
import util from "utils/utils";

import { EmptyBox } from "components/lib/UI";

import type {
  LinkChartDataFn,
  ShortenedLink,
  ShortenedUrlsQueryParams,
} from "../types";
import LinkRow from "./LinkRow";
import { ShortLinkCreator } from "./Links";

type ManageLinksModalProps = {
  isOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
};

const ManageLinksModal = ({
  isOpen,
  onOpen = () => {},
  onClose = () => {},
}: ManageLinksModalProps) => {
  const [links, setLinks] = useState<ShortenedLink[]>([]);
  const [hasLinksResults, setHasLinksResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const listQuery = useMemo<ShortenedUrlsQueryParams>(
    () => ({ page: 1, limit: 100 }),
    [],
  );
  const [creatingNewLink, setCreatingNewLink] = useState(false);
  const [newLinkState, setNewLinkState] = useState<{
    url: string;
    path: string;
    description: string;
  }>({ url: "", path: "", description: "" });
  const [creatingNewLinkLoading, setCreatingNewLinkLoading] = useState(false);
  const user = useAppSelector((state) => state.user);
  const orgId = user?.ownerOrganisation?._id;
  const emptyChartData = useCallback<LinkChartDataFn>(() => null, []);

  const getShortenedUrls = useCallback(() => {
    if (!orgId) return;

    setLoading(true);
    api.organisations.getShortenedUrls(
      orgId,
      listQuery,
      (data) => {
        setLinks(data.shortenedUrls);
        setHasLinksResults((prev) => prev || data.shortenedUrls.length > 0);
        setLoading(false);
      },
      () => {
        setLoading(false);
      },
    );
  }, [orgId, listQuery]);

  const filteredLinks = useMemo(() => {
    if (!searchQuery) return links;

    const query = searchQuery.toLowerCase();
    return links.filter(
      (link) =>
        link.path.toLowerCase().includes(query) ||
        link.url.toLowerCase().includes(query),
    );
  }, [links, searchQuery]);

  useEffect(() => {
    if (isOpen) {
      getShortenedUrls();
    }
  }, [getShortenedUrls, isOpen]);

  const createUrl = useCallback(() => {
    if (!orgId) return;

    setCreatingNewLinkLoading(true);
    api.organisations.createShortenedUrl(
      orgId,
      newLinkState,
      (shortenedUrl) => {
        setNewLinkState({ url: "", path: "", description: "" });
        setCreatingNewLinkLoading(false);
        setCreatingNewLink(false);
        setLinks((prevLinks) => [shortenedUrl, ...prevLinks]);
        setHasLinksResults(true);
      },
      (err) => {
        setCreatingNewLinkLoading(false);
        toast.error(err.message);
      },
    );
  }, [orgId, newLinkState]);

  const deleteUrl = useCallback(
    (urlId: string) => {
      if (!orgId) return;

      util
        .confirm(
          "Really delete this URL?",
          "The URL and associated analytics data will be immediately removed. Be careful - someone else can take your short URL once you delete it.",
        )
        .then(() => {
          api.organisations.deleteShortenedUrl(
            orgId,
            urlId,
            () => {
              setLinks((prevLinks) => {
                const nextLinks = prevLinks.filter(
                  (link) => link._id !== urlId,
                );
                if (nextLinks.length === 0) {
                  setHasLinksResults(false);
                }
                return nextLinks;
              });
            },
            (err) => toast.error(err.message),
          );
        })
        .catch(() => {});
    },
    [orgId],
  );

  const copyLink = useCallback((link: string) => {
    if (window.navigator?.clipboard) {
      window.navigator.clipboard
        .writeText(`${sdiClickDomain}/${link}`)
        .then(() => toast.success("Link copied to your clipboard"))
        .catch(() => toast.error("Failed to copy link to clipboard"));
    } else {
      toast.error("Your browser does not support copying");
    }
  }, []);

  return (
    <Modal
      mountNode={document.getElementById("semantic-modal-mount-node")}
      open={isOpen}
      onClose={onClose}
      onOpen={onOpen}
    >
      <Modal.Header>
        {user?.ownerOrganisation?.name}&apos;s Shortened Links
      </Modal.Header>
      <Modal.Content scrolling>
        {loading && !hasLinksResults ?
          <Loader inline="centered" active />
        : null}
        {!creatingNewLink ?
          <Button
            primary
            fluid
            icon="add"
            content="Create new shortened link"
            onClick={() => setCreatingNewLink(true)}
            style={{ marginBottom: 10 }}
          />
        : <Segment>
            <ShortLinkCreator
              url={newLinkState.url}
              path={newLinkState.path}
              description={newLinkState.description}
              setUrl={(url) => setNewLinkState({ ...newLinkState, url })}
              setPath={(path) => setNewLinkState({ ...newLinkState, path })}
              setDescription={(description) =>
                setNewLinkState({ ...newLinkState, description })
              }
            />
            <Button
              primary
              fluid
              icon="add"
              content="Create shortened link using these details"
              onClick={createUrl}
              loading={creatingNewLinkLoading}
              style={{ marginTop: 10 }}
            />
          </Segment>
        }
        {hasLinksResults ?
          <>
            <Input
              fluid
              placeholder="Search by URL or path"
              loading={loading}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Table basic="very">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Link</Table.HeaderCell>
                  <Table.HeaderCell>Created</Table.HeaderCell>
                  <Table.HeaderCell>Total clicks</Table.HeaderCell>
                  <Table.HeaderCell />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filteredLinks.map((link) => (
                  <LinkRow
                    key={link._id}
                    link={link}
                    chartData={emptyChartData}
                    copyLink={copyLink}
                    deleteUrl={deleteUrl}
                    disableReferrals
                  />
                ))}
              </Table.Body>
            </Table>
          </>
        : null}
        {!loading && links.length === 0 ?
          <EmptyBox title="No shortened links found" />
        : null}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Done</Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ManageLinksModal;
